import React, {
  useEffect,
  useState,
} from "react";

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function CollaborativeProjectCard({
  group,
  busy,
  onRequest,
  isFR,
}) {
  const requestStatus =
    clean(
      group?.joinRequest
        ?.status
    ).toLowerCase();

  return (
    <article
      className="ns-card"
      style={{
        padding: 16,
        borderRadius: 16,
        border:
          "1px solid rgba(5,150,105,0.18)",
        borderLeft:
          "4px solid rgba(5,150,105,0.52)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: "0.1em",
          textTransform:
            "uppercase",
          color: "#047857",
        }}
      >
        {isFR
          ? "COLLABORATIF"
          : "COLLABORATIVE"}
      </div>

      <h4
        className="ns-card-title"
        style={{
          margin: 0,
          fontSize: 16,
        }}
      >
        {clean(group?.title) ||
          (
            isFR
              ? "Manuel sans titre"
              : "Untitled Textbook"
          )}
      </h4>

      <p
        className="ns-card-meta"
        style={{
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {isFR
          ? "En tant que Writer enregistré, vous pouvez demander à collaborer. Le propriétaire doit approuver la demande avant que vous puissiez proposer du contenu."
          : "As a registered Writer, you can request to collaborate. The Owner must approve the request before you can submit proposals."}
      </p>

      {requestStatus ? (
        <strong
          style={{
            fontSize: 13,
            color:
              requestStatus ===
                "pending"
                ? "#92400e"
                : "#475569",
          }}
        >
          {requestStatus ===
          "pending"
            ? (
                isFR
                  ? "Demande en attente"
                  : "Request pending"
              )
            : requestStatus ===
              "rejected"
            ? (
                isFR
                  ? "Demande refusée"
                  : "Request declined"
              )
            : (
                isFR
                  ? "Demande traitée"
                  : "Request decided"
              )}
        </strong>
      ) : (
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            busy ||
            group
              ?.canRequestToJoin !==
              true
          }
          onClick={onRequest}
          style={{
            alignSelf:
              "flex-start",
          }}
        >
          {busy
            ? (
                isFR
                  ? "Envoi…"
                  : "Sending…"
              )
            : (
                isFR
                  ? "Demander à collaborer"
                  : "Request to Collaborate"
              )}
        </button>
      )}
    </article>
  );
}

export function TextbookCollaborativeDiscovery({
  api,
  isFR = false,
}) {
  const [groups, setGroups] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [busyId, setBusyId] =
    useState("");

  async function load() {
    try {
      setLoading(true);
      setError("");

      const payload =
        await api
          .getTextbookCollaborativeProjects();

      setGroups(
        Array.isArray(
          payload?.collaborativeProjects
        )
          ? payload.collaborativeProjects
          : []
      );
    } catch (err) {
      setGroups([]);
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger les manuels collaboratifs."
            : "Could not load Collaborative Textbooks."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function requestToJoin(
    group
  ) {
    const id =
      clean(group?.id);

    if (!id) {
      return;
    }

    try {
      setBusyId(id);
      setError("");

      const payload =
        await api
          .requestTextbookCollaborativeMembership(
            id
          );

      const joinRequest =
        payload
          ?.joinRequest ||
        null;

      setGroups(
        (current) =>
          current.map(
            (item) =>
              clean(item?.id) === id
                ? {
                    ...item,
                    joinRequest,
                    canRequestToJoin:
                      false,
                  }
                : item
          )
      );
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’envoyer cette demande."
            : "Could not send this join request."
        )
      );
    } finally {
      setBusyId("");
    }
  }

  if (
    !loading &&
    !error &&
    groups.length === 0
  ) {
    return null;
  }

  return (
    <section
      data-textbook-collaborative-projects="true"
      style={{
        marginBottom: 24,
        padding: 20,
        borderRadius: 20,
        border:
          "2px solid rgba(5,150,105,0.34)",
        background:
          "linear-gradient(145deg, rgba(236,253,245,0.98), rgba(248,255,252,0.98))",
        boxShadow:
          "0 10px 24px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
              padding: "5px 10px",
              borderRadius: 999,
              background:
                "rgba(5,150,105,0.12)",
              color: "#047857",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
            }}
          >
            {isFR
              ? "PARTICIPATION COLLABORATIVE"
              : "COLLABORATIVE PARTICIPATION"}
          </div>

          <h3
            className="ns-section-title"
            style={{
              margin: "0 0 5px",
              fontSize: 22,
            }}
          >
            {isFR
              ? "Manuels collaboratifs"
              : "Collaborative Textbooks"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
            }}
          >
            {isFR
              ? "Découvrez les manuels collaboratifs pour lesquels les Writers enregistrés peuvent demander à collaborer."
              : "Discover Collaborative Textbooks where registered Writers can request to collaborate."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={loading}
          onClick={load}
        >
          {isFR
            ? "Actualiser"
            : "Refresh"}
        </button>
      </div>

      {error ? (
        <div
          role="alert"
          style={{
            color: "#b91c1c",
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div
          className="ns-card"
          style={{
            padding: 16,
          }}
        >
          {isFR
            ? "Chargement des manuels collaboratifs…"
            : "Loading Collaborative Textbooks…"}
        </div>
      ) : groups.length ===
        0 ? (
        <div
          className="ns-card"
          style={{
            padding: 18,
            border:
              "1px dashed rgba(5,150,105,0.42)",
            background:
              "rgba(255,255,255,0.82)",
          }}
        >
          <strong>
            {isFR
              ? "Aucun autre manuel collaboratif n’est disponible pour vous actuellement."
              : "No other Collaborative Textbook is available to you right now."}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "7px 0 0",
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Les projets que vous possédez ou auxquels vous appartenez déjà apparaissent dans Mes manuels ci-dessous. Pour tester une demande d’adhésion, connectez-vous avec un autre compte qui n’est pas encore membre."
              : "Projects you own or have been approved to collaborate on appear under My Textbooks below. To test a request, sign in with another Writer account that is not already a member."}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(250px, 305px))",
            gap: 14,
          }}
        >
          {groups.map(
            (group) => {
              const id =
                clean(group?.id);

              return (
                <CollaborativeProjectCard
                  key={id}
                  group={group}
                  busy={
                    busyId === id
                  }
                  onRequest={() =>
                    requestToJoin(
                      group
                    )
                  }
                  isFR={isFR}
                />
              );
            }
          )}
        </div>
      )}
    </section>
  );
}

export function TextbookCollaborativeRequests({
  api,
  projectId,
  isFR = false,
}) {
  const [requests, setRequests] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [busyId, setBusyId] =
    useState("");

  async function load() {
    const id =
      clean(projectId);

    if (!id) {
      setRequests([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload =
        await api
          .getTextbookJoinRequests(
            id,
            {
              status:
                "pending",
            }
          );

      setRequests(
        Array.isArray(
          payload?.joinRequests
        )
          ? payload
              .joinRequests
          : []
      );
    } catch (err) {
      setRequests([]);
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger les demandes de collaboration."
            : "Could not load collaboration requests."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function decide(
    request,
    action
  ) {
    const id =
      clean(request?.id);

    if (!id) {
      return;
    }

    try {
      setBusyId(id);
      setError("");

      await api
        .decideTextbookJoinRequest(
          projectId,
          id,
          action
        );

      setRequests(
        (current) =>
          current.filter(
            (item) =>
              clean(item?.id) !== id
          )
      );
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de traiter cette demande."
            : "Could not decide this collaboration request."
        )
      );
    } finally {
      setBusyId("");
    }
  }


  return (
    <section
      data-textbook-collaboration-requests="true"
      data-textbook-owner-management-strip="true"
      style={{
        padding: "14px 16px",
        borderRadius: 14,
        marginBottom: 18,
        border:
          "1px solid rgba(5,150,105,0.22)",
        borderLeft:
          "4px solid #059669",
        background:
          "linear-gradient(90deg, rgba(236,253,245,0.82), rgba(248,255,252,0.96))",
        boxShadow: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            COLLABORATION
          </div>

          <h3
            className="ns-textbook-academic-title"
            style={{
              margin: "4px 0 4px",
              fontSize: 20,
              lineHeight: 1.15,
            }}
          >
            {isFR
              ? "Demandes de collaboration"
              : "Collaboration Requests"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
            }}
          >
            {isFR
              ? "Le propriétaire reste le gardien académique. Approuvez uniquement les Writers autorisés à proposer du contenu."
              : "The Owner is the academic gatekeeper. Approve only Writers you want to permit to submit proposals."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={loading}
          onClick={load}
        >
          {isFR
            ? "Actualiser"
            : "Refresh"}
        </button>
      </div>

      {error ? (
        <div
          role="alert"
          style={{
            color: "#b91c1c",
            fontWeight: 700,
            marginTop: 10,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <p
          className="ns-card-meta"
          style={{
            marginBottom: 0,
          }}
        >
          {isFR
            ? "Chargement des demandes…"
            : "Loading requests…"}
        </p>
      ) : requests.length ===
        0 ? (
        <p
          className="ns-card-meta"
          style={{
            marginBottom: 0,
          }}
        >
          {isFR
            ? "Aucune demande de collaboration en attente."
            : "No pending collaboration requests."}
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 10,
            marginTop: 12,
          }}
        >
          {requests.map(
            (request) => {
              const id =
                clean(request?.id);

              const requester =
                request
                  ?.requester ||
                {};

              const label =
                clean(
                  requester?.name
                ) ||
                clean(
                  requester
                    ?.username
                ) ||
                clean(
                  requester?.email
                ) ||
                (
                  isFR
                    ? "Utilisateur"
                    : "User"
                );

              return (
                <div
                  key={id}
                  style={{
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    flexWrap:
                      "wrap",
                    padding:
                      "10px 12px",
                    border:
                      "1px solid rgba(5,150,105,0.16)",
                    borderRadius: 10,
                    background:
                      "rgba(255,255,255,0.78)",
                  }}
                >
                  <div>
                    <strong>
                      {label}
                    </strong>

                    {clean(
                      requester
                        ?.email
                    ) &&
                    clean(
                      requester
                        ?.email
                    ) !==
                      label ? (
                      <div
                        className="ns-card-meta"
                      >
                        {
                          requester
                            .email
                        }
                      </div>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display:
                        "flex",
                      gap: 8,
                    }}
                  >
                    <button
                      className="ns-button ns-button-primary"
                      type="button"
                      disabled={
                        busyId === id
                      }
                      onClick={() =>
                        decide(
                          request,
                          "approve"
                        )
                      }
                    >
                      {isFR
                        ? "Approuver"
                        : "Approve"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={
                        busyId === id
                      }
                      onClick={() =>
                        decide(
                          request,
                          "reject"
                        )
                      }
                    >
                      {isFR
                        ? "Refuser"
                        : "Reject"}
                    </button>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
    </section>
  );
}
